import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { PACKAGE_FOR_FUNDING } from "../lib/payments";
import PaymentMethodSelector from "../components/PaymentMethodSelector";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const PLAN_OPTIONS = [
  { id: "ai_report", label: "AI Funding Report — $139 one-time", amount: 139 },
  { id: "pro_monthly", label: "WealthFirst PRO — $45 / month", amount: 45 },
  { id: "pro_annual", label: "WealthFirst PRO Annual — $365 / year", amount: 365 },
  { id: "autopilot", label: "WealthFirst AutoPilot — $91 / month · 7-day trial", amount: 91 },
];

const REVENUE_OPTIONS = ["Pre-revenue", "Under $50k", "$50k – $250k", "$250k – $1M", "$1M – $5M", "$5M+"];
const TIMELINE_OPTIONS = ["Immediately", "Within 30 days", "Within 90 days", "Within 6 months", "Just exploring"];

const initial = {
  plan: "autopilot",
  name: "",
  email: "",
  phone: "",
  business_name: "",
  business_type: "",
  annual_revenue: "",
  funding_goal: "",
  timeline: "",
  notes: "",
};

export default function FundingApply() {
  const [params] = useSearchParams();
  const initialPlan = params.get("plan") && PLAN_OPTIONS.find((p) => p.id === params.get("plan")) ? params.get("plan") : "autopilot";
  const [form, setForm] = useState({ ...initial, plan: initialPlan });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/funding/apply", form);
      setDone(true);
      toast.success("Application submitted. We will be in touch within one business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    const selectedPlan = PLAN_OPTIONS.find((p) => p.id === form.plan);
    const packageId = PACKAGE_FOR_FUNDING[form.plan];
    return (
      <div data-testid="funding-applied" className="max-w-2xl mx-auto px-6 pt-32 pb-24">
        <div className="text-center mb-10 reveal">
          <CheckCircle2 size={48} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
          <p className="eyebrow mb-4">Application Received · Complete Payment</p>
          <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
            Welcome to the <em style={{ color: "var(--brass)" }}>Funding Portal</em>.
          </h1>
          <p className="text-base leading-relaxed" style={{ color: "var(--ink-muted)" }}>
            Activate your plan now by paying{" "}
            <strong style={{ color: "var(--brass)" }}>${selectedPlan?.amount?.toLocaleString()}</strong>.
            Choose any rail — cards, bank debit, Cash App, Klarna, USDC stablecoin, or an NFT/Web3 wallet.
          </p>
        </div>
        {packageId && selectedPlan && (
          <PaymentMethodSelector
            packageId={packageId}
            amount={selectedPlan.amount}
            label={selectedPlan.label}
            metadata={{ plan: form.plan, name: form.name, email: form.email }}
          />
        )}
      </div>
    );
  }

  return (
    <div data-testid="funding-apply-page" className="max-w-[900px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      <div className="mb-12 reveal">
        <p className="eyebrow mb-4">Step 1 of 3 · Submit Form</p>
        <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
          Apply to the <em style={{ color: "var(--brass)" }}>Funding Portal</em>.
        </h1>
        <p className="text-base max-w-2xl leading-relaxed" style={{ color: "var(--ink-muted)" }}>
          A few details to start. Our AI agents will analyze your profile and lender qualifications, then
          deliver your personalized funding plan.
        </p>
      </div>

      <hr className="hairline mb-10" />

      <form onSubmit={submit} className="space-y-7" data-testid="funding-form">
        <div>
          <label className="eyebrow block mb-3">Select Your Plan *</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PLAN_OPTIONS.map((p) => (
              <label
                key={p.id}
                data-testid={`plan-${p.id}`}
                className="flex items-start gap-3 p-4 cursor-pointer transition-all"
                style={{
                  border: form.plan === p.id ? "1px solid var(--brass)" : "1px solid var(--hairline)",
                  background: form.plan === p.id ? "var(--surface)" : "transparent",
                }}
              >
                <input
                  type="radio"
                  name="plan"
                  checked={form.plan === p.id}
                  onChange={() => set("plan", p.id)}
                  className="mt-1 accent-[var(--brass)]"
                />
                <span className="text-sm" style={{ color: form.plan === p.id ? "var(--brass)" : "var(--ink)" }}>
                  {p.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="eyebrow block mb-2">Full Name *</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-line" data-testid="funding-name" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Email *</label>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-line" data-testid="funding-email" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Phone</label>
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-line" data-testid="funding-phone" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Business Name</label>
            <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className="input-line" data-testid="funding-business-name" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Business Type / NAICS</label>
            <input value={form.business_type} onChange={(e) => set("business_type", e.target.value)} className="input-line" placeholder="e.g. Real Estate Holding · 531120" data-testid="funding-business-type" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Annual Revenue</label>
            <select value={form.annual_revenue} onChange={(e) => set("annual_revenue", e.target.value)} className="input-line bg-transparent" data-testid="funding-revenue">
              <option value="" style={{ background: "var(--surface)" }}>— Select —</option>
              {REVENUE_OPTIONS.map((r) => <option key={r} value={r} style={{ background: "var(--surface)" }}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow block mb-2">Funding Goal (USD)</label>
            <input value={form.funding_goal} onChange={(e) => set("funding_goal", e.target.value)} placeholder="e.g. $250,000" className="input-line" data-testid="funding-goal" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Timeline</label>
            <select value={form.timeline} onChange={(e) => set("timeline", e.target.value)} className="input-line bg-transparent" data-testid="funding-timeline">
              <option value="" style={{ background: "var(--surface)" }}>— Select —</option>
              {TIMELINE_OPTIONS.map((t) => <option key={t} value={t} style={{ background: "var(--surface)" }}>{t}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="eyebrow block mb-2">Anything else we should know?</label>
          <textarea
            rows={5}
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Existing credit, prior funding rounds, entity structure, certifications, etc."
            className="w-full bg-transparent p-3 text-base outline-none"
            style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }}
            data-testid="funding-notes"
          />
        </div>

        <div className="flex justify-between items-center pt-2 flex-wrap gap-4">
          <p className="text-xs" style={{ color: "var(--ink-faint)" }}>
            Your data is encrypted in transit. We respond within one business day.
          </p>
          <button type="submit" disabled={busy} className="btn-brass" data-testid="funding-submit">
            {busy ? "Submitting…" : "Submit Application"}
          </button>
        </div>
      </form>
    </div>
  );
}
